import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { getIdentity } from '../../internal';
import { MAX_LIMIT } from '../../services/utils';
const URL_REGEX_UID_PART = '@currentUser';
const URL_REGEX_END_PART = '($|(/|\\?).*)';
const SERVICE_ENDPT = '/preferences/preferences/';
export const SERVICE_URL = `${SERVICE_ENDPT}@currentUser`;
const SERVICE_URL_REGEXP = new RegExp(`^${SERVICE_ENDPT}${URL_REGEX_UID_PART}${URL_REGEX_END_PART}`);
// max java integer
const MAX_ITEMS_LIMIT = MAX_LIMIT;
const ERRONEOUS_PREF_ID = '_hc_xx_';
const MAX_INIT_ATTEMPTS = 5;

// IJqXhr is a contract compatible with openui-framework.
// https://api.jquery.com/jQuery.ajax/
// ICompatiblePrefCacheHelper represents cache helper contract compatible with
// older version of cache in openui-framework.
// IPrefCacheHelper represents reloadable cache helper contract.
// JqXhrAdapter is an adapter on axios response for compatibility with openui-framework.
export class JqXhrAdapter {
  constructor(axRes) {
    this.axRes = axRes;
  }
  always(cb) {
    if (this.axRes.status < 400) {
      cb.apply(this, [this.responseJSON, this.statusText, this]);
    } else {
      cb.apply(this, [this, this.statusText, this.statusText]);
    }
    return this;
  }
  done(cb) {
    cb.apply(this, [this.responseJSON, this.statusText, this]);
    return this;
  }
  fail(cb) {
    cb.apply(this, [this, this.statusText, this.statusText]);
    return this;
  }
  getAllResponseHeaders() {
    return this.axRes.headers;
  }
  getResponseHeader(name) {
    return this.axRes.headers[name.toLowerCase()];
  }
  get readyState() {
    return this.axRes.request.readyState;
  }
  get responseJSON() {
    return this.axRes.data;
  }
  get responseText() {
    return this.axRes.request.responseText;
  }
  setRequestHeader() {
    // no op.
    return this;
  }
  get status() {
    return this.axRes.status.toString();
  }
  statusCode() {
    return this;
  }
  get statusText() {
    if (this.axRes.status < 300) {
      return 'success';
    }
    return this.axRes.statusText;
  }
  then(s, f) {
    if (this.axRes.status < 300) {
      s.apply(this, [this.responseJSON, this.statusText, this]);
    } else if (f !== undefined) {
      f.apply(this, [this, this.statusText, this.statusText]);
    }
    return this;
  }
}

// CompatibleError represents error object compatible with that of openui-framework.
export class CompatibleError extends JqXhrAdapter {
  constructor(axErr) {
    super(axErr.response);
    _defineProperty(this, "axErr", void 0);
    this.axErr = axErr;
  }
  get name() {
    return this.axErr.name;
  }
  get message() {
    return this.axErr.message;
  }
  get stack() {
    return this.axErr.stack;
  }
  get config() {
    return this.axErr.config;
  }
  get response() {
    return this.axErr.response;
  }
  get code() {
    return this.axErr.code;
  }
  get request() {
    return this.axErr.request;
  }
  get isAxiosError() {
    return this.axErr.isAxiosError;
  }
  toJSON() {
    return this.axErr.toJSON();
  }
}

//
// @class
// @name PrefCacheHelper
// User preference cache helper. Used by cache to deal with data.
// This class is CRITICAL for backward compatibility and can be shared
// across versions of Html Commons. Any changes to this class MUST be
// vetted for regression.
//
// TODO periodic refresh.
//
export class PrefCacheHelper {
  constructor(axiosInstance) {
    this.axiosInstance = axiosInstance;
    /* private variables */
    _defineProperty(this, "dataInitialized", false);
    _defineProperty(this, "dataInitializing", false);
    _defineProperty(this, "errorDataInitialized", false);
    _defineProperty(this, "errorDataInitializing", false);
    _defineProperty(this, "uid", void 0);
    _defineProperty(this, "initAttempts", 0);
    _defineProperty(this, "cachedJqXhr", void 0);
    _defineProperty(this, "cachedData", void 0);
    _defineProperty(this, "cachedErrObjects", void 0);
    _defineProperty(this, "alternateServiceUrl", SERVICE_URL);
    _defineProperty(this, "instanceUrlRegEx", SERVICE_URL_REGEXP);
    this.init();
  }

  // getData loads preferences data from server.
  getData() {
    return this.axiosInstance.get(SERVICE_URL, {
      headers: {
        Accept: 'application/json'
      },
      params: {
        limit: MAX_ITEMS_LIMIT
      },
      cache: false
    });
  }

  // init initializes cache.
  async init() {
    if (this.dataInitializing || this.errorDataInitializing || this.initAttempts > MAX_INIT_ATTEMPTS) {
      return;
    }
    this.initAttempts++;
    this.dataInitialized = false;
    this.dataInitializing = true;
    try {
      const resp = await this.getData();
      this.cachedData = Object.seal(resp.data);
      this.cachedJqXhr = new JqXhrAdapter(resp);

      // load user id
      if (!this.uid) {
        const uIDType = await getIdentity();
        this.uid = uIDType.id;
        if (this.uid) {
          this.instanceUrlRegEx = new RegExp(`^${SERVICE_ENDPT}(@currentUser|${this.uid})${URL_REGEX_END_PART}`);
          this.alternateServiceUrl = SERVICE_ENDPT + this.uid;
        }
      }
      this.dataInitializing = false;
      this.dataInitialized = true;
    } catch (error) {
      this.dataInitializing = false;
      this.cachedData = undefined;
      this.dataInitialized = false;
    }
    if (!this.cachedErrObjects) {
      this.errorDataInitialized = false;
      this.errorDataInitializing = true;
      try {
        await this.axiosInstance.get(`${SERVICE_URL}/${ERRONEOUS_PREF_ID}`, {
          headers: {
            Accept: 'application/json'
          },
          cache: false
        });
      } catch (error) {
        const axErr = error;
        this.cachedErrObjects = [Object.seal(new CompatibleError(axErr)), 'error', 'Not Found'];
      }
      this.errorDataInitialized = true;
      this.errorDataInitializing = false;
    }
  }

  // initialized return true if cache is initialized. False otherwise.
  initialized() {
    return this.dataInitialized && this.errorDataInitialized;
  }

  // extractPrefId extracts the preference id from input path.
  extractPrefId(path) {
    if (path.indexOf(SERVICE_URL) === 0) {
      return path.substring(SERVICE_URL.length + 1);
    }
    if (path.indexOf(this.alternateServiceUrl) === 0) {
      return path.substring(this.alternateServiceUrl.length + 1);
    }
  }
  get textStatus() {
    if (this.cachedJqXhr) {
      return this.cachedJqXhr.statusText;
    }
    return '';
  }

  // addToCache adds data from input response to cache.
  addToCache(respData, _, jq) {
    let contentTypeHdr = jq.getResponseHeader('Content-Type');
    if (!contentTypeHdr || contentTypeHdr.indexOf('json') === -1 || !this.dataInitialized) return;
    contentTypeHdr = contentTypeHdr.toLowerCase();
    // check if data is collection.
    if (contentTypeHdr.indexOf('vnd.sas.collection') !== -1) {
      const data = respData;
      const newItems = data.items.filter(newItem => {
        return this.cachedData.items.every((item, i) => {
          if (item.id === newItem.id) {
            this.cachedData.items[i] = newItem;
            return false;
          }
          return true;
        });
      });
      this.cachedData.items.push(...newItems);
    } else if (contentTypeHdr.indexOf('vnd.sas.preference') !== -1) {
      const data = respData;
      const isNew = this.cachedData.items.every((item, i) => {
        if (item.id === data.id) {
          this.cachedData.items[i] = data;
          return false;
        }
        return true;
      });
      isNew && this.cachedData.items.push(data);
    }
    this.cachedData.count = this.cachedData.items.length;
  }

  // removeFromCache removes cached data associated with input request.
  removeFromCache(c) {
    if (this.dataInitialized === false && !c.url) return;
    const parsedUri = new URL(c.url, self.location.origin);
    const prefId = this.extractPrefId(parsedUri.pathname);
    const parsedData = !prefId && c.data && typeof c.data === 'string' ? JSON.parse(c.data) : c.data;
    if (parsedData && Array.isArray(parsedData.items)) {
      // was a bulk delete
      for (const itm of parsedData.items) {
        for (let ci = this.cachedData.items.length; ci--;) {
          if (this.cachedData.items[ci].id === itm.id) {
            this.cachedData.items.splice(ci, 1);
            this.cachedData.count = this.cachedData.items.length;
            break;
          }
        }
      }
    } else {
      const matchingIndex = this.cachedData.items.findIndex(item => item.id === prefId);
      if (matchingIndex !== -1) {
        this.cachedData.items.splice(matchingIndex, 1);
        this.cachedData.count = this.cachedData.items.length;
      }
    }
  }
  async reload() {
    if (!this.initialized()) {
      this.init();
      return;
    }
    // reload data.
    try {
      const resp = await this.getData();
      this.cachedData = Object.seal(resp.data);
      this.cachedJqXhr = new JqXhrAdapter(resp);
    } catch (error) {
      // TODO log error.
      // ignore.
    }
  }
}
//# sourceMappingURL=prefHelper.js.map